import { useContext, useEffect, useMemo, useState } from 'react';
import {
	AlertTriangle,
	Check,
	CheckCircle2,
	Clock3,
	Copy,
	KeyRound,
	RefreshCw,
	RotateCcw,
	Save,
	Send,
	Webhook,
	XCircle
} from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { GlobalContext } from '../context/GlobalContext';
import { ContainerSpinner } from '../components/Spinner';
import { EmptyState } from '../components/EmptyState';
import { Dialog } from '../components/Dialog';
import Input from '../components/Input';
import Notice from '../components/Notice';
import { Button } from '../shadcn/Button';
import { Checkbox } from '../shadcn/Checkbox';
import { Switch } from '../shadcn/Switch';
import { cn } from '../shadcn/utils';
import { format_date_time } from '../utils/formatters';
import { callAPI, type StoreWebhookConfig, type StoreWebhookDelivery, type StoreWebhookEventType } from '../hooks/api';

const EVENT_OPTIONS: Array<{
	value: StoreWebhookEventType;
	label: string;
	description: string;
}> = [
	{
		value: 'sale.completed',
		label: 'Venta completada',
		description: 'Se envía cuando Krece termina de registrar una venta.'
	},
	{
		value: 'sale.cancelled',
		label: 'Venta cancelada',
		description: 'Se envía cuando la tienda elimina una venta correctamente.'
	}
];

const DELIVERY_STATUS: Record<StoreWebhookDelivery['status'], { label: string; className: string; Icon: typeof CheckCircle2 }> = {
	delivered: {
		label: 'Entregado',
		className: 'bg-emerald-500/10 text-emerald-400 ring-emerald-500/20 light:text-emerald-700',
		Icon: CheckCircle2
	},
	failed: {
		label: 'Fallido',
		className: 'bg-red-500/10 text-red-400 ring-red-500/20 light:text-red-700',
		Icon: XCircle
	},
	pending: {
		label: 'Pendiente',
		className: 'bg-amber-500/10 text-amber-400 ring-amber-500/20 light:text-amber-700',
		Icon: Clock3
	},
	delivering: {
		label: 'Enviando',
		className: 'bg-primary-500/10 text-primary-300 ring-primary-500/20 light:text-primary-700',
		Icon: Send
	}
};

const ERROR_LABELS: Record<string, string> = {
	timeout: 'Tiempo de espera agotado',
	connection_error: 'Error de conexión',
	blocked_destination: 'Destino bloqueado',
	http_3xx: 'Redirección no permitida',
	http_4xx: 'El endpoint rechazó la solicitud',
	http_5xx: 'El endpoint respondió con un error',
	configuration_error: 'Error de configuración',
	unknown_error: 'Error desconocido'
};

type Feedback = { tone: 'success' | 'error'; message: string };

export default function Integraciones() {
	const { userData } = useContext(GlobalContext);
	const [webhook, setWebhook] = useState<StoreWebhookConfig | null>(null);
	const [deliveries, setDeliveries] = useState<StoreWebhookDelivery[]>([]);
	const [url, setUrl] = useState('');
	const [isActive, setIsActive] = useState(true);
	const [eventTypes, setEventTypes] = useState<StoreWebhookEventType[]>(EVENT_OPTIONS.map((event) => event.value));
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [historyLoading, setHistoryLoading] = useState(false);
	const [historyError, setHistoryError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const [testing, setTesting] = useState(false);
	const [rotating, setRotating] = useState(false);
	const [showRotateConfirmation, setShowRotateConfirmation] = useState(false);
	const [signingSecret, setSigningSecret] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);
	const [copyError, setCopyError] = useState<string | null>(null);
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [loadRequest, setLoadRequest] = useState(0);
	const isMasterStore = userData?.master_user === true;

	useEffect(() => {
		let cancelled = false;
		if (!userData?.email || !isMasterStore) {
			setLoading(false);
			return;
		}

		setLoading(true);
		setLoadError(null);
		setHistoryError(null);
		void Promise.all([
			callAPI('https://api.krece.app/storeprofile/integrations/webhook/', 'GET', undefined, userData),
			callAPI('https://api.krece.app/storeprofile/integrations/webhook/deliveries/', 'GET', { limit: 25 }, userData)
		]).then(([[configData, configError], [deliveryData, deliveryError]]) => {
			if (cancelled) return;

			if (configError || !configData) {
				setLoadError(configError || 'Ha ocurrido un error. Intente más tarde.');
			} else {
				const savedWebhook = configData.webhook;
				setWebhook(savedWebhook);
				setUrl(savedWebhook?.url || '');
				setIsActive(savedWebhook?.is_active ?? true);
				setEventTypes(
					savedWebhook?.event_types.length ? savedWebhook.event_types : EVENT_OPTIONS.map((event) => event.value)
				);
			}

			if (deliveryError || !deliveryData) {
				setHistoryError(deliveryError || 'Ha ocurrido un error. Intente más tarde.');
			} else {
				setDeliveries(deliveryData.deliveries);
			}
			setLoading(false);
		});

		return () => {
			cancelled = true;
		};
	}, [isMasterStore, loadRequest, userData]);

	const isValidUrl = useMemo(() => {
		try {
			const parsed = new URL(url.trim());
			return parsed.protocol === 'https:' && Boolean(parsed.hostname);
		} catch {
			return false;
		}
	}, [url]);

	const hasUnsavedChanges = useMemo(() => {
		if (!webhook) return true;
		const sameEvents =
			eventTypes.length === webhook.event_types.length &&
			eventTypes.every((eventType) => webhook.event_types.includes(eventType));
		return url.trim() !== webhook.url || isActive !== webhook.is_active || !sameEvents;
	}, [eventTypes, isActive, url, webhook]);

	const toggleEvent = (eventType: StoreWebhookEventType) => {
		setEventTypes((current) =>
			current.includes(eventType) ? current.filter((value) => value !== eventType) : [...current, eventType]
		);
	};

	const refreshDeliveries = async () => {
		if (!userData) return;
		setHistoryLoading(true);
		setHistoryError(null);
		const [data, error] = await callAPI(
			'https://api.krece.app/storeprofile/integrations/webhook/deliveries/',
			'GET',
			{ limit: 25 },
			userData
		);
		if (error || !data) setHistoryError(error || 'Ha ocurrido un error. Intente más tarde.');
		else setDeliveries(data.deliveries);
		setHistoryLoading(false);
	};

	const saveWebhook = async () => {
		if (!userData || !isValidUrl || eventTypes.length === 0) return;
		setSaving(true);
		setFeedback(null);
		const [data, error] = await callAPI(
			'https://api.krece.app/storeprofile/integrations/webhook/',
			'PUT',
			{ url: url.trim(), is_active: isActive, event_types: eventTypes },
			userData
		);
		if (error || !data) {
			setFeedback({
				tone: 'error',
				message: error || 'Ha ocurrido un error. Intente más tarde.'
			});
		} else {
			setWebhook(data.webhook);
			setUrl(data.webhook.url);
			if (data.signing_secret) {
				setSigningSecret(data.signing_secret);
				setCopied(false);
				setCopyError(null);
			}
			setFeedback({
				tone: 'success',
				message: 'La configuración fue guardada.'
			});
		}
		setSaving(false);
	};

	const sendTest = async () => {
		if (!userData || !webhook) return;
		setTesting(true);
		setFeedback(null);
		const [data, error] = await callAPI(
			'https://api.krece.app/storeprofile/integrations/webhook/test/',
			'POST',
			undefined,
			userData
		);
		if (error || !data) {
			setFeedback({
				tone: 'error',
				message: error || 'Ha ocurrido un error. Intente más tarde.'
			});
		} else if (data.delivered) {
			setFeedback({
				tone: 'success',
				message: `El evento de prueba fue entregado${data.response_status ? ` con HTTP ${data.response_status}` : ''}.`
			});
			setWebhook((current) => (current ? { ...current, last_tested_at: new Date().toISOString() } : current));
		} else {
			setWebhook((current) => (current ? { ...current, last_tested_at: new Date().toISOString() } : current));
			const result = data.response_status ? `HTTP ${data.response_status}` : formatErrorCode(data.error_code);
			setFeedback({
				tone: 'error',
				message: `El endpoint no respondió correctamente. ${result}`
			});
		}
		setTesting(false);
	};

	const rotateSecret = async () => {
		if (!userData || !webhook) return;
		setRotating(true);
		setFeedback(null);
		const [data, error] = await callAPI(
			'https://api.krece.app/storeprofile/integrations/webhook/rotate-secret/',
			'POST',
			undefined,
			userData
		);
		if (error || !data) {
			setFeedback({
				tone: 'error',
				message: error || 'Ha ocurrido un error. Intente más tarde.'
			});
			setShowRotateConfirmation(false);
		} else {
			setWebhook(data.webhook);
			setSigningSecret(data.signing_secret);
			setCopied(false);
			setCopyError(null);
			setShowRotateConfirmation(false);
			setFeedback({
				tone: 'success',
				message: 'La clave de firma fue regenerada.'
			});
		}
		setRotating(false);
	};

	const copySecret = async () => {
		if (!signingSecret) return;
		setCopied(false);
		setCopyError(null);
		try {
			await navigator.clipboard.writeText(signingSecret);
			setCopied(true);
		} catch {
			setCopyError('No se pudo copiar la clave. Selecciónala y cópiala manualmente.');
		}
	};

	if (!userData || loading) return <ContainerSpinner />;
	if (!isMasterStore) return <Navigate to="/" replace />;

	if (loadError) {
		return (
			<div className="p-4 md:p-6">
				<EmptyState icon={AlertTriangle} title="No pudimos cargar la integración" subtitle={loadError} />
				<div className="mt-4 flex justify-center">
					<Button className="!w-auto" onClick={() => setLoadRequest((value) => value + 1)}>
						Intentar de nuevo
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-full space-y-4 p-4 md:p-6">
			<header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
				<div className="flex items-start gap-3">
					<div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary-500/10 text-primary-300 ring-1 ring-inset ring-primary-500/20 light:text-primary-700">
						<Webhook className="size-5" />
					</div>
					<div>
						<h1 className="text-2xl font-bold tracking-tight text-neutral-0">Integración por webhook</h1>
						<p className="mt-1 max-w-2xl text-sm leading-relaxed text-neutral-400">
							Recibe notificaciones firmadas cuando una venta se completa o se cancela.
						</p>
					</div>
				</div>
				{webhook && (
					<span
						className={cn(
							'inline-flex w-fit items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ring-1 ring-inset',
							webhook.is_active
								? 'bg-emerald-500/10 text-emerald-400 ring-emerald-500/20 light:text-emerald-700'
								: 'bg-neutral-900 text-neutral-400 ring-neutral-800'
						)}
					>
						<span className="size-2 rounded-full bg-current" />
						{webhook.is_active ? 'Activa' : 'Inactiva'}
					</span>
				)}
			</header>

			<section className="rounded-2xl border border-neutral-800 bg-neutral-925 p-5 shadow-sm md:p-6">
				<div className="flex flex-col gap-5">
					<div>
						<h2 className="text-lg font-semibold tracking-tight text-neutral-100">Configuración</h2>
						<p className="mt-1 text-sm text-neutral-400">
							Krece enviará solicitudes POST en formato JSON a este endpoint.
						</p>
					</div>

					<div className="space-y-2">
						<Input
							placeholder="https://tu-dominio.com/webhooks/krece"
							floatingLabel="Endpoint HTTPS"
							type="url"
							value={url}
							clearable
							Icon={<Webhook className="size-4 text-neutral-400" />}
							validation={(value) => {
								try {
									return new URL(value).protocol === 'https:';
								} catch {
									return false;
								}
							}}
							onChange={setUrl}
						/>
						<p className="text-xs text-neutral-500">Solo se permiten endpoints públicos con HTTPS.</p>
					</div>

					<div className="flex items-center justify-between gap-4 rounded-xl border border-neutral-800 bg-neutral-950/40 p-4">
						<div>
							<p className="text-sm font-medium text-neutral-100">Activar notificaciones</p>
							<p className="mt-1 text-xs text-neutral-500">
								Al reactivar no se enviarán ventas ocurridas mientras estuvo apagada.
							</p>
						</div>
						<Switch checked={isActive} onCheckedChange={setIsActive} aria-label="Activar notificaciones" />
					</div>

					<fieldset>
						<legend className="text-sm font-medium text-neutral-200">Eventos</legend>
						<div className="mt-2 grid gap-2 sm:grid-cols-2">
							{EVENT_OPTIONS.map((event) => {
								const checked = eventTypes.includes(event.value);
								return (
									<label
										key={event.value}
										className={cn(
											'flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors',
											checked
												? 'border-primary-500/40 bg-primary-500/[0.06]'
												: 'border-neutral-800 bg-neutral-950/30 hover:bg-neutral-900/50'
										)}
									>
										<Checkbox
											checked={checked}
											onCheckedChange={() => toggleEvent(event.value)}
											className="mt-0.5"
										/>
										<span>
											<span className="block text-sm font-medium text-neutral-100">{event.label}</span>
											<span className="mt-1 block text-xs leading-relaxed text-neutral-500">
												{event.description}
											</span>
										</span>
									</label>
								);
							})}
						</div>
						{eventTypes.length === 0 && <p className="mt-2 text-xs text-red-400">Selecciona al menos un evento.</p>}
					</fieldset>

					{!webhook && (
						<div className="flex items-start gap-3 rounded-xl border border-neutral-800 bg-neutral-950/40 p-4">
							<KeyRound className="mt-0.5 size-5 shrink-0 text-primary-300 light:text-primary-700" />
							<div>
								<p className="text-sm font-medium text-neutral-100">La clave se genera al guardar</p>
								<p className="mt-1 text-xs leading-relaxed text-neutral-500">
									Krece mostrará la clave de firma una sola vez cuando crees esta integración.
								</p>
							</div>
						</div>
					)}

					{webhook?.has_signing_secret && (
						<div className="flex flex-col gap-4 rounded-xl border border-neutral-800 bg-neutral-950/40 p-4 sm:flex-row sm:items-center sm:justify-between">
							<div className="flex items-start gap-3">
								<KeyRound className="mt-0.5 size-5 shrink-0 text-primary-300 light:text-primary-700" />
								<div>
									<p className="text-sm font-medium text-neutral-100">Clave de firma</p>
									<p className="mt-1 text-xs leading-relaxed text-neutral-500">
										Versión {webhook.signing_secret_version || 1}. La clave actual no se puede consultar.
										Regenerarla invalida la anterior.
									</p>
								</div>
							</div>
							<Button
								variant="secondary"
								className="!w-auto shrink-0"
								Icon={<RotateCcw className="size-4" />}
								onClick={() => setShowRotateConfirmation(true)}
							>
								Regenerar clave
							</Button>
						</div>
					)}

					{feedback && (
						<div
							aria-live="polite"
							className={cn(
								'flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm',
								feedback.tone === 'success'
									? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300 light:text-emerald-700'
									: 'border-red-500/20 bg-red-500/10 text-red-300 light:text-red-700'
							)}
						>
							{feedback.tone === 'success' ? (
								<CheckCircle2 className="mt-0.5 size-4 shrink-0" />
							) : (
								<AlertTriangle className="mt-0.5 size-4 shrink-0" />
							)}
							<span>{feedback.message}</span>
						</div>
					)}

					<div className="flex flex-col-reverse gap-2 border-t border-neutral-800 pt-5 sm:flex-row sm:items-center sm:justify-between">
						<div className="text-xs text-neutral-500">
							{webhook && hasUnsavedChanges
								? 'Guarda los cambios antes de enviar una prueba.'
								: webhook?.last_tested_at
									? `Última prueba: ${formatDate(webhook.last_tested_at)}`
									: 'Todavía no se ha enviado una prueba.'}
						</div>
						<div className="flex flex-col gap-2 sm:flex-row">
							{webhook && (
								<Button
									variant="secondary"
									className="!w-auto"
									loading={testing}
									disabled={hasUnsavedChanges}
									Icon={<Send className="size-4" />}
									onClick={sendTest}
								>
									Enviar prueba
								</Button>
							)}
							<Button
								variant="primary"
								className="!w-auto"
								loading={saving}
								disabled={!isValidUrl || eventTypes.length === 0}
								Icon={<Save className="size-4" />}
								onClick={saveWebhook}
							>
								Guardar configuración
							</Button>
						</div>
					</div>
				</div>
			</section>

			<section className="overflow-hidden rounded-2xl border border-neutral-800 bg-neutral-925 shadow-sm">
				<div className="flex items-center justify-between gap-3 border-b border-neutral-800 p-5 md:px-6">
					<div>
						<h2 className="text-lg font-semibold tracking-tight text-neutral-100">Entregas recientes</h2>
						<p className="mt-1 text-sm text-neutral-400">Últimos eventos enviados al endpoint configurado.</p>
					</div>
					<Button
						variant="secondary"
						size="icon"
						className="!w-10 shrink-0"
						loading={historyLoading}
						onClick={refreshDeliveries}
						aria-label="Actualizar entregas"
						title="Actualizar"
					>
						<RefreshCw className="size-4" />
					</Button>
				</div>

				{historyError ? (
					<div className="p-5 md:p-6">
						<EmptyState icon={AlertTriangle} title="No pudimos cargar las entregas" subtitle={historyError} />
					</div>
				) : deliveries.length === 0 ? (
					<EmptyState
						icon={Webhook}
						title="Todavía no hay entregas"
						subtitle="Los eventos aparecerán aquí después de completar o cancelar una venta."
					/>
				) : (
					<div className="overflow-x-auto">
						<table className="w-full table-fixed text-sm sm:table-auto">
							<thead>
								<tr className="border-b border-neutral-800 bg-neutral-900/60 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
									<th className="w-[55%] px-5 py-3 sm:w-auto md:px-6">Evento</th>
									<th className="hidden px-4 py-3 md:table-cell">Venta</th>
									<th className="px-4 py-3">Estado</th>
									<th className="hidden px-4 py-3 sm:table-cell">Intentos</th>
									<th className="hidden px-4 py-3 lg:table-cell">Resultado</th>
									<th className="hidden px-5 py-3 text-right md:table-cell md:px-6">Fecha</th>
								</tr>
							</thead>
							<tbody>
								{deliveries.map((delivery) => (
									<DeliveryRow key={delivery.id} delivery={delivery} />
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>

			{signingSecret && (
				<Dialog
					title="Guarda tu clave de firma"
					closable={false}
					cerrarButton={false}
					disableOverlayClose
					className="w-[min(560px,90vw)]"
				>
					<div className="space-y-4">
						<div className="flex items-start gap-3 rounded-lg border border-amber-500/20 bg-amber-500/10 p-3 text-sm text-amber-300 light:text-amber-700">
							<AlertTriangle className="mt-0.5 size-4 shrink-0" />
							<p>Esta clave se muestra una sola vez. Guárdala antes de cerrar esta ventana.</p>
						</div>
						<div className="flex items-center gap-2 rounded-lg border border-neutral-800 bg-neutral-950 p-3">
							<code className="min-w-0 flex-1 select-all break-all text-xs text-neutral-200">{signingSecret}</code>
							<button
								type="button"
								onClick={copySecret}
								className={cn(
									'flex size-9 shrink-0 items-center justify-center rounded-lg transition-colors',
									copied
										? 'bg-emerald-500/15 text-emerald-400 light:text-emerald-700'
										: 'bg-neutral-900 text-neutral-300 hover:bg-neutral-800'
								)}
								aria-label="Copiar clave"
								title={copied ? 'Copiada' : 'Copiar'}
							>
								{copied ? <Check className="size-4" /> : <Copy className="size-4" />}
							</button>
						</div>
						{copyError && (
							<div role="alert">
								<Notice message={copyError} variant="error" className="light:text-red-700" />
							</div>
						)}
						<p className="text-sm leading-relaxed text-neutral-400">
							Tu servidor debe usarla para verificar la firma HMAC-SHA256 de cada evento recibido.
						</p>
						<div className="flex justify-end">
							<Button
								variant="primary"
								className="!w-auto"
								onClick={() => {
									setSigningSecret(null);
									setCopied(false);
									setCopyError(null);
								}}
							>
								Ya la guardé
							</Button>
						</div>
					</div>
				</Dialog>
			)}

			{showRotateConfirmation && (
				<Dialog
					title="Regenerar clave de firma"
					onClose={() => setShowRotateConfirmation(false)}
					cerrarButton={false}
					disableOverlayClose
				>
					<div
						className="max-w-md space-y-5"
						onKeyDown={(event) => {
							// Keep Enter on the focused button instead of Dialog's submit shortcut.
							if (event.key === 'Enter') event.stopPropagation();
						}}
					>
						<p className="text-sm leading-relaxed text-neutral-300">
							La clave actual dejará de validar eventos inmediatamente. Actualiza tu servidor con la nueva clave
							antes de enviar más ventas.
						</p>
						<div className="flex justify-end gap-2">
							<Button variant="secondary" className="!w-auto" onClick={() => setShowRotateConfirmation(false)}>
								Cancelar
							</Button>
							<Button
								type="submit"
								variant="destructive"
								className="!w-auto"
								loading={rotating}
								onClick={rotateSecret}
							>
								Regenerar clave
							</Button>
						</div>
					</div>
				</Dialog>
			)}
		</div>
	);
}

function DeliveryRow({ delivery }: { delivery: StoreWebhookDelivery }) {
	const status = DELIVERY_STATUS[delivery.status];
	const StatusIcon = status.Icon;
	const result = delivery.last_response_status
		? `HTTP ${delivery.last_response_status}`
		: formatErrorCode(delivery.last_error_code);

	return (
		<tr className="border-b border-neutral-800/60 last:border-b-0">
			<td className="px-5 py-3.5 md:px-6">
				<p className="font-medium text-neutral-100">{formatEventType(delivery.event_type)}</p>
				<p
					className="mt-0.5 max-w-[220px] truncate font-mono text-[11px] text-neutral-500"
					title={delivery.event_id || undefined}
				>
					{delivery.event_id || 'Sin ID'}
				</p>
			</td>
			<td className="hidden px-4 py-3.5 md:table-cell">
				<span
					className="block max-w-[180px] truncate font-mono text-xs text-neutral-300"
					title={delivery.sale_id || undefined}
				>
					{delivery.sale_id || 'Prueba'}
				</span>
			</td>
			<td className="px-4 py-3.5">
				<span
					className={cn(
						'inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium ring-1 ring-inset',
						status.className
					)}
				>
					<StatusIcon className="size-3.5" />
					{status.label}
				</span>
			</td>
			<td className="hidden px-4 py-3.5 text-neutral-300 sm:table-cell">{delivery.attempt_count}</td>
			<td className="hidden max-w-[260px] px-4 py-3.5 lg:table-cell">
				<p className="text-xs text-neutral-300">{result || 'Sin respuesta'}</p>
				{delivery.last_response_excerpt && (
					<p className="mt-0.5 truncate text-[11px] text-neutral-500" title={delivery.last_response_excerpt}>
						{delivery.last_response_excerpt}
					</p>
				)}
			</td>
			<td className="hidden whitespace-nowrap px-5 py-3.5 text-right text-xs text-neutral-400 md:table-cell md:px-6">
				{formatDate(delivery.created_at)}
			</td>
		</tr>
	);
}

function formatEventType(eventType: StoreWebhookDelivery['event_type']) {
	if (eventType === 'sale.completed') return 'Venta completada';
	if (eventType === 'sale.cancelled') return 'Venta cancelada';
	return eventType || 'Evento';
}

function formatErrorCode(errorCode: string | null) {
	if (!errorCode) return '';
	return ERROR_LABELS[errorCode] || errorCode;
}

function formatDate(value: string | null) {
	if (!value) return 'Sin fecha';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return 'Sin fecha';
	return format_date_time(value);
}
